import { HTMLAttributes, forwardRef, ReactNode } from 'react';

export type CardVariant = 'default' | 'interactive' | 'elevated';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: CardPadding;
  header?: ReactNode;
  footer?: ReactNode;
}

const BASE = 'rounded-card border backdrop-blur-xl transition-colors duration-fast ease-smooth';

const VARIANT_CLASSES: Record<CardVariant, string> = {
  default:     'bg-surface-raised/40 border-border-subtle',
  interactive: 'bg-surface-raised/40 border-border-subtle hover:border-accent-primary/30 hover:bg-surface-raised/60 cursor-pointer',
  elevated:    'bg-surface-raised/70 border-border shadow-glass',
};

const PADDING_CLASSES: Record<CardPadding, string> = {
  none: '',
  sm:   'p-4',
  md:   'p-card',   // 24px
  lg:   'p-8',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = 'default', padding = 'md', header, footer, className, children, ...rest },
  ref,
) {
  const hasSlots = Boolean(header || footer);

  // Jeśli są header/footer - wyłączamy outer padding, robimy padding w slotach
  const outerPadding = hasSlots ? PADDING_CLASSES.none : PADDING_CLASSES[padding];
  const innerPadding = PADDING_CLASSES[padding];

  return (
    <div
      ref={ref}
      className={[BASE, VARIANT_CLASSES[variant], outerPadding, className].filter(Boolean).join(' ')}
      {...rest}
    >
      {header && (
        <div className={`${innerPadding} pb-3 border-b border-border-subtle`}>
          {header}
        </div>
      )}
      {hasSlots ? (
        <div className={innerPadding}>{children}</div>
      ) : (
        children
      )}
      {footer && (
        <div className={`${innerPadding} pt-3 border-t border-border-subtle`}>
          {footer}
        </div>
      )}
    </div>
  );
});
